import { CaseData, DelayedEvent, VerdictOutcome, VerdictType } from '../core/types';
import { ENERGY_MAX, computeCurrentEnergy } from '../core/energy';
import { supabase } from '../lib/supabase';

interface CaseRowV2 {
  id: string;
  difficulty: 'easy' | 'medium' | 'hard';
  applicant: CaseData['applicant'];
  documents: CaseData['documents'];
  testimony: string[];
  issues: CaseData['issues'];
  questions: CaseData['questions'];
  verdict_outcomes: Partial<Record<VerdictType, VerdictOutcome>>;
  delayed_events: DelayedEvent[];
  testimony_en?: string[];
  issues_en?: CaseData['issues'];
  questions_en?: CaseData['questions'];
  documents_en?: CaseData['documents'];
}

function rowToCaseV2(row: CaseRowV2): CaseData {
  return {
    id: row.id,
    difficulty: row.difficulty,
    applicant: row.applicant,
    documents: row.documents,
    testimony: row.testimony,
    issues: row.issues,
    questions: row.questions ?? [],
    verdictOutcomes: row.verdict_outcomes ?? {},
    delayedEvents: row.delayed_events ?? [],
    testimony_en: row.testimony_en,
    issues_en: row.issues_en,
    questions_en: row.questions_en,
    documents_en: row.documents_en,
  };
}

export async function getCases(): Promise<CaseData[]> {
  const { data, error } = await supabase.from('cases_v2').select('*');
  if (error) throw new Error(`Failed to load cases: ${error.message}`);
  return (data as CaseRowV2[]).map(rowToCaseV2);
}

export function pickRandomCase(cases: CaseData[]): CaseData {
  return cases[Math.floor(Math.random() * cases.length)];
}

export async function getEnergy(userId: string): Promise<{ energy: number; energyUpdatedAt: Date }> {
  const { data } = await supabase
    .from('profiles')
    .select('energy, energy_updated_at')
    .eq('user_id', userId)
    .maybeSingle();
  if (!data) return { energy: ENERGY_MAX, energyUpdatedAt: new Date() };
  const stored: number = data.energy ?? ENERGY_MAX;
  const updatedAt = new Date(data.energy_updated_at ?? Date.now());
  const current = computeCurrentEnergy(stored, updatedAt, new Date());
  return { energy: current, energyUpdatedAt: updatedAt };
}

export async function saveEnergy(userId: string, energy: number): Promise<void> {
  await supabase
    .from('profiles')
    .update({ energy, energy_updated_at: new Date().toISOString() })
    .eq('user_id', userId);
}

export interface PlayerProgress {
  currentCaseId: string | null;
  usedCaseIds: string[];
}

export async function getProgress(userId: string): Promise<PlayerProgress> {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('current_case_id, used_case_ids')
      .eq('user_id', userId)
      .maybeSingle();
    
    if (error) {
      console.warn('Could not load progress from cloud (migration 012 might be missing):', error.message);
      return { currentCaseId: null, usedCaseIds: [] };
    }

    return {
      currentCaseId: data?.current_case_id ?? null,
      usedCaseIds: data?.used_case_ids ?? [],
    };
  } catch (err) {
    console.error('Progress sync error:', err);
    return { currentCaseId: null, usedCaseIds: [] };
  }
}

export async function saveProgress(userId: string, progress: PlayerProgress): Promise<void> {
  try {
    const { error } = await supabase
      .from('profiles')
      .update({
        current_case_id: progress.currentCaseId,
        used_case_ids: progress.usedCaseIds,
      })
      .eq('user_id', userId);
    
    if (error) {
      console.warn('Could not save progress to cloud (migration 012 might be missing):', error.message);
    }
  } catch (err) {
    console.error('Progress save error:', err);
  }
}

export async function saveScore(userId: string, day: number, stats: { kpi: number; careerRisk: number; proceduralRisk: number }, isGameOver: boolean = false): Promise<void> {
  try {
    await supabase.from('scores').upsert({
      user_id: userId,
      day: day,
      total_score: stats.kpi, // v1 compatibility
      accuracy: stats.kpi,    // v1 compatibility
      kpi: stats.kpi,
      career_risk: stats.careerRisk,
      procedural_risk: stats.proceduralRisk,
      is_game_over: isGameOver,
    }, { onConflict: 'user_id,day' });
  } catch (err) {
    console.error('Failed to save score:', err);
  }
}
